/**
 * Module for types and helpers specific to roles supported by the FGA v2 model.
 * @module rolesV2
 */

import {Product} from '../types';

export const DISABLED_USER = 'disabled' as const;

export enum OrgRole {
  OWNER = 'org_owner',
  ADMIN = 'org_admin',
  IAM_ADMIN = 'org_iam_admin',
  USER = 'org_user',
  BILLING_READER = 'billing_reader',
}

export enum DisplayRole {
  OWNER = 'Owner',
  ADMIN = 'Admin',
  IAM_ADMIN = 'IAM Admin',
  USER = 'User',
  READ_ONLY = 'Read Only',
  NOACCESS = 'No Access',
  EDITOR = 'Editor',
  BILLING_READER = 'Billing Reader',
}

export type OrgRoles = OrgRole | DisplayRole;

export enum WorkspaceRole {
  OWNER = 'ws_owner',
  ADMIN = 'ws_admin',
  USER = 'ws_user',
}

// TODO: This should replace product_noaccess roles
export enum NonFunctionalRole {
  NOACCESS = 'noaccess',
}

export interface WorkspaceRoleFull {
  workspaceName: string;
  role: WorkspaceRole | NonFunctionalRole;
}

export enum ProductRoleName {
  ADMIN = 'admin',
  READER = 'reader',
  USER = 'user',
  NOACCESS = 'noaccess',
  EDITOR = 'editor',
}

export enum ProductRole {
  ADMIN = 'product_admin',
  EDITOR = 'product_editor',
  READER = 'product_reader',
  USER = 'product_user',
  NOACCESS = 'product_noaccess',
}

export interface ProductRoleFull {
  workspaceName: string;
  product: Product;
  role: ProductRole;
}

export enum SystemRole {
  ADMIN = 'cloud_admin',
  ADMIN_READER = 'cloud_admin_reader',
  BILLING_READER = 'cloud_billing_reader',
  ENTITLEMENTS_READER = 'cloud_entitlements_reader',
  ENTITLEMENTS_ADMIN = 'cloud_entitlements_admin',
  CICD = 'cloud_cicd',
  DEVELOPER = 'cloud_developer',
  PURGE = 'cloud_purge',
  RC_ADMIN = 'cloud_rc_admin',
  SE = 'cloud_se',
  SUPPORT = 'cloud_support',
  SMTP_ADMIN = 'cloud_smtp_admin',
  /**
   * Synthetic display-only role.
   * Excluded from {@link ASSIGNABLE_SYSTEM_ROLES} — never send it to FGA.
   */
  USER = 'cloud_user',
}

/**
 * System roles that FGA accepts in a tuple write, i.e. the relations on `type system` that declare a
 * direct type restriction (`[user, client, ...]`) in `openfga/modeling/CriblCloud-v2.openfga`.
 */
export const ASSIGNABLE_SYSTEM_ROLES: readonly SystemRole[] = Object.freeze(
  Object.values(SystemRole).filter((role) => role !== SystemRole.USER),
);

const assignableSystemRoleSet: ReadonlySet<string> = new Set<string>(ASSIGNABLE_SYSTEM_ROLES);

/** Narrows an untrusted string (e.g. a request body entry) to a role FGA will accept in a write. */
export const isAssignableSystemRole = (role: unknown): role is SystemRole => {
  return typeof role === 'string' && assignableSystemRoleSet.has(role);
};

export const validProductRoles = Object.values(ProductRole);
export const validProductRoleSet = new Set<ProductRole>(validProductRoles);

export const isInvalidProductRole = (product: Product, role: ProductRole): boolean => {
  return (
    (product === Product.SEARCH && role === ProductRole.READER) ||
    (product === Product.LAKE && role === ProductRole.USER)
  );
};

export const mapProductRole = (product: Product, productRoleName: ProductRoleName): ProductRoleName => {
  if (product === Product.SEARCH && productRoleName === ProductRoleName.READER) return ProductRoleName.USER;
  if (product === Product.LAKE && productRoleName === ProductRoleName.USER) return ProductRoleName.READER;

  return productRoleName;
};

export const toProductRole = (product: Product, productRoleName: ProductRoleName): ProductRole => {
  const mapped = mapProductRole(product, productRoleName);
  return `product_${mapped}` as ProductRole;
};
